import React, { useEffect, useRef } from "react";
import {
  Link,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Undo,
  Redo,
  ContentCopy,
  Spellcheck,
  CheckCircle,
  Block,
} from "@mui/icons-material";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import {
  cleanDocument,
  documentText,
  safeAnswerDocument,
} from "../utils/answerDocument";

export default function TypedAnswerEditor({
  text = "",
  document,
  onChange,
  disabled = false,
  allowSpelling = true,
  allowClipboard = true,
  showPermissionStatus = true,
}) {
  const current = useRef({ onChange, allowClipboard });
  current.current = { onChange, allowClipboard };
  const blockClipboard = (_, event) => {
    if (current.current.allowClipboard) return false;
    event.preventDefault();
    return true;
  };
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
        strike: false,
        link: false,
        underline: false,
      }),
      Underline,
    ],
    content: safeAnswerDocument(document, text),
    editable: !disabled,
    editorProps: {
      handleDOMEvents: {
        copy: blockClipboard,
        cut: blockClipboard,
        paste: blockClipboard,
        drop: blockClipboard,
      },
    },
    onUpdate: ({ editor: value }) => {
      const doc = cleanDocument(value.getJSON());
      current.current.onChange(documentText(doc), doc);
    },
  });
  useEffect(() => {
    if (!editor) return;
    const desired = cleanDocument(safeAnswerDocument(document, text));
    if (
      JSON.stringify(cleanDocument(editor.getJSON())) !==
      JSON.stringify(desired)
    ) {
      editor.commands.setContent(desired, {
        emitUpdate: false,
      });
    }
  }, [editor, text, document]);
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
    editor.setOptions({
      editorProps: {
        ...editor.options.editorProps,
        attributes: {
          role: "textbox",
          "aria-label": "Answer text",
          "aria-multiline": "true",
          spellcheck: String(allowSpelling),
          autocorrect: allowSpelling ? "on" : "off",
          autocapitalize: allowSpelling ? "sentences" : "off",
        },
      },
    });
  }, [editor, disabled, allowSpelling]);
  const buttons = [
    ["Bold", "toggleBold", FormatBold],
    ["Italic", "toggleItalic", FormatItalic],
    ["Underline", "toggleUnderline", FormatUnderlined],
    ["Bulleted list", "toggleBulletList", FormatListBulleted],
    ["Numbered list", "toggleOrderedList", FormatListNumbered],
    ["Undo", "undo", Undo],
    ["Redo", "redo", Redo],
  ];
  return (
    <Box
      sx={{
        my: 2,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        bgcolor: "common.white",
      }}
    >
      <Stack
        direction="row"
        flexWrap="wrap"
        role="toolbar"
        aria-label="Answer formatting"
        sx={{
          p: 1,
          alignItems: "center",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        {buttons.map(([label, command, Icon]) => (
          <Tooltip key={command} title={label}>
            <span>
              <IconButton
                aria-label={label}
                disabled={disabled || !editor}
                sx={{ border: 0, "&:hover": { border: 0 } }}
                onClick={() => editor.chain().focus()[command]().run()}
              >
                <Icon />
              </IconButton>
            </span>
          </Tooltip>
        ))}
        <Stack
          direction="row"
          alignItems="center"
          sx={{ ml: "auto", maxWidth: "100%" }}
        >
          {showPermissionStatus && (
            <Stack direction="row" spacing={1} sx={{ mx: 1 }}>
              {[
                ["Copy and paste", allowClipboard, ContentCopy],
                ["Spelling assistance", allowSpelling, Spellcheck],
              ].map(([label, allowed, Icon]) => {
                const description = `${label} ${allowed ? "enabled" : "disabled"} by the teacher for this assignment.`;
                const StatusIcon = allowed ? CheckCircle : Block;
                return (
                  <Tooltip key={label} title={description}>
                    <Box
                      component="span"
                      role="img"
                      aria-label={description}
                      tabIndex={0}
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        p: 1,
                        color: allowed ? "success.main" : "text.secondary",
                      }}
                    >
                      <Icon fontSize="small" />
                      <StatusIcon
                        sx={{
                          position: "absolute",
                          bottom: 1,
                          right: 0,
                          fontSize: 12,
                          bgcolor: "common.white",
                          borderRadius: "50%",
                        }}
                      />
                    </Box>
                  </Tooltip>
                );
              })}
            </Stack>
          )}
          <Link
            href="/help/tutorials/first-answer#typed-answer"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ pl: 1, py: 0.5 }}
          >
            Help with typed answers
          </Link>
        </Stack>
      </Stack>
      <Box
        sx={{
          "& .tiptap": {
            minHeight: 180,
            p: 2,
            outline: "none",
            overflowWrap: "anywhere",
          },
          "& .tiptap:focus-visible": {
            outline: "none",
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
      {(!allowSpelling || !allowClipboard) && (
        <Typography variant="body2" sx={{ px: 2, pb: 1 }}>
          {!allowSpelling && "Spelling assistance is disabled. "}
          {!allowClipboard && "Copy, cut and paste are disabled."}
        </Typography>
      )}
    </Box>
  );
}
